import React, { useEffect } from 'react';
import { i18n } from '@lingui/core';
import { I18nProvider as LinguiProvider } from '@lingui/react';
import { en as enPlurals, es as esPlurals, zh as zhPlurals } from 'make-plural/plurals';
import type { Locale } from '../bridge/protocol';
import { sanitizeLocale } from '../bridge/protocol';
import { messages as en } from './locales/en/messages';
import { messages as es } from './locales/es/messages';
import { messages as zh } from './locales/zh/messages';


// Compiled catalogs (`yarn extract` → translate `.po` → `yarn compile`).
// Every flow's strings land in these shared per-locale catalogs, so adding a
// flow only means re-running extract/compile — no change here. Locales and the
// plural-rule wiring mirror lum-web (`src/components/i18n/i18nService.js`).


i18n.loadLocaleData({
  en: { plurals: enPlurals },
  es: { plurals: esPlurals },
  zh: { plurals: zhPlurals },
});
i18n.load({ en, es, zh });
i18n.activate('en');

type Props = {
  locale: Locale;
  children: React.ReactNode;
};

export const I18nProvider: React.FC<Props> = ({ locale, children }) => {
  // `locale` comes from the bridge `init` (untrusted postMessage input); the
  // bridge already sanitizes it, but guard here too so the provider never
  // activates an empty/unknown catalog when used directly (e.g. in tests).
  const active = sanitizeLocale(locale);

  useEffect(() => {
    if (i18n.locale !== active) {
      i18n.activate(active);
    }
  }, [active]);

  return <LinguiProvider i18n={i18n}>{children}</LinguiProvider>;
};
